import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import styles from './SearchableSelect.module.css';

const MAX_SHOWN = 100;

/**
 * Compact dropdown you can type in to narrow the choices, for filter rows.
 * options: [{ id, name }]; value is an id, '' means the `emptyLabel` (all) choice.
 */
const SearchableSelect = ({ options, value, onChange, emptyLabel = 'All', ariaLabel, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const listId = useId();

  const selected = useMemo(
    () => options.find((option) => String(option.id) === String(value)),
    [options, value],
  );

  // First entry is always the "all" choice.
  const choices = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matches = term
      ? options.filter((option) => `${option.name} ${option.id}`.toLowerCase().includes(term))
      : options;
    return [{ id: '', name: emptyLabel }, ...matches];
  }, [emptyLabel, options, search]);

  const close = () => {
    setOpen(false);
    setSearch('');
  };

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) close();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const choose = (option) => {
    onChange(option.id);
    close();
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      close();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const shown = Math.min(choices.length, MAX_SHOWN + 1);
      setOpen(true);
      setHighlight((current) => (current + (event.key === 'ArrowDown' ? 1 : -1) + shown) % shown);
    } else if (event.key === 'Enter' && open) {
      event.preventDefault();
      if (choices[highlight]) choose(choices[highlight]);
    }
  };

  const shown = choices.slice(0, MAX_SHOWN + 1);

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        className={`${className} ${styles.input}`}
        value={open ? search : (selected?.name || '')}
        placeholder={open && selected ? selected.name : emptyLabel}
        title={selected?.name || emptyLabel}
        onFocus={() => { setOpen(true); setHighlight(0); }}
        // The input keeps focus after a choice, so a click must reopen the list too.
        onClick={() => setOpen(true)}
        onChange={(event) => { setSearch(event.target.value); setOpen(true); setHighlight(event.target.value ? 1 : 0); }}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />
      {open ? (
        <ul className={styles.list} role="listbox" id={listId}>
          {shown.map((option, index) => (
            <li
              key={option.id || 'all'}
              role="option"
              aria-selected={String(option.id) === String(value || '')}
              className={`${styles.option} ${index === highlight ? styles.highlight : ''} ${String(option.id) === String(value || '') ? styles.selected : ''}`}
              // mousedown, not click: fires before the input loses focus.
              onMouseDown={(event) => { event.preventDefault(); choose(option); }}
              onMouseEnter={() => setHighlight(index)}
            >
              {option.name}
              {option.id && option.name !== option.id ? <span className={styles.optionId}>{option.id}</span> : null}
            </li>
          ))}
          {choices.length === 1 && search ? <li className={styles.note}>No match for “{search}”</li> : null}
          {choices.length > shown.length ? (
            <li className={styles.note}>{choices.length - shown.length} more — keep typing to narrow</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
};

export default SearchableSelect;
